import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Chip, IconButton, Button, MenuItem, Select, FormControl, InputLabel, Pagination,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { userAdminAPI } from '../services/api';
import { formatKstDate } from '../utils/datetime';

const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(n);

const Users: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // 검색어·상태·페이지를 URL에 둬서 상세 보기 후 돌아와도 보던 목록 그대로 유지
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const suspended = searchParams.get('suspended') ?? '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const [searchInput, setSearchInput] = useState(search);
  const limit = 20;

  useEffect(() => setSearchInput(search), [search]);

  const updateList = (next: { search?: string; suspended?: string; page?: number }) => {
    const merged = { search, suspended, page, ...next };
    const params = new URLSearchParams();
    if (merged.search) params.set('search', merged.search);
    if (merged.suspended) params.set('suspended', merged.suspended);
    if (merged.page > 1) params.set('page', String(merged.page));
    setSearchParams(params, { replace: true });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search, suspended, page],
    queryFn: async () => (await userAdminAPI.getAll({ search, suspended: suspended || undefined, page, limit })).data,
  });

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>회원 관리</Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small" label="이메일/닉네임 검색" value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') updateList({ search: searchInput, page: 1 }); }}
        />
        <Button variant="contained" onClick={() => updateList({ search: searchInput, page: 1 })}>검색</Button>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>상태</InputLabel>
          <Select value={suspended} label="상태" onChange={(e) => updateList({ suspended: e.target.value, page: 1 })}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="false">정상</MenuItem>
            <MenuItem value="true">정지</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>이메일/닉네임</TableCell>
              <TableCell>로그인</TableCell>
              <TableCell align="right">직급</TableCell>
              <TableCell align="right">보유 포인트</TableCell>
              <TableCell align="right">보너스</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>가입일</TableCell>
              <TableCell align="center">상세</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.items?.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.id}</TableCell>
                <TableCell>{u.nickname || u.email || '-'}</TableCell>
                <TableCell>{u.provider}</TableCell>
                <TableCell align="right">Lv.{u.level}</TableCell>
                <TableCell align="right">{fmt(u.points)}</TableCell>
                <TableCell align="right">{fmt(u.bonusPoints)}</TableCell>
                <TableCell>
                  <Chip label={u.isSuspended ? '정지' : '정상'} color={u.isSuspended ? 'error' : 'success'} size="small" />
                </TableCell>
                <TableCell>{formatKstDate(u.createdAt)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => navigate(`/users/${u.id}`, { state: { listSearch: location.search } })}><ViewIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && !data?.items?.length && (
              <TableRow><TableCell colSpan={9} align="center">회원이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => updateList({ page: p })} />
        </Box>
      )}
    </Box>
  );
};

export default Users;
